import { useId } from 'react';
import { AlertCircle } from 'lucide-react';
import cn from '../../lib/cn';

/**
 * Label + control + error wrapper shared by Input, Select and Textarea.
 * Children receive the generated ids so validation stays accessible.
 */
function FieldShell({ label, error, hint, required, id, children, className }) {
  return (
    <div className={cn('w-full', className)}>
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-ink-800">
        {label}
        {required && <span className="ml-0.5 text-brand-600">*</span>}
      </label>
      {children}
      {error ? (
        <p id={`${id}-error`} className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-red-600">
          <AlertCircle className="h-3.5 w-3.5 flex-none" />
          {error}
        </p>
      ) : (
        hint && <p className="mt-1.5 text-xs text-ink-400">{hint}</p>
      )}
    </div>
  );
}

export function Input({ label, error, hint, required, className, ...rest }) {
  const id = useId();
  return (
    <FieldShell label={label} error={error} hint={hint} required={required} id={id} className={className}>
      <input
        id={id}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
        className={cn('field', error && 'field-error')}
        {...rest}
      />
    </FieldShell>
  );
}

export function Textarea({ label, error, hint, required, className, rows = 4, ...rest }) {
  const id = useId();
  return (
    <FieldShell label={label} error={error} hint={hint} required={required} id={id} className={className}>
      <textarea
        id={id}
        rows={rows}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
        className={cn('field resize-y', error && 'field-error')}
        {...rest}
      />
    </FieldShell>
  );
}

export function Select({ label, error, hint, required, options = [], placeholder, className, ...rest }) {
  const id = useId();
  return (
    <FieldShell label={label} error={error} hint={hint} required={required} id={id} className={className}>
      <select
        id={id}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
        className={cn('field appearance-none bg-[length:16px] bg-[right_1rem_center] bg-no-repeat pr-10', error && 'field-error')}
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23637082' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")",
        }}
        {...rest}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </FieldShell>
  );
}

/** Multi-select rendered as toggleable chips — used for e-waste categories. */
export function ChipGroup({ label, error, hint, required, options, value = [], onChange, className }) {
  const id = useId();
  const toggle = (option) =>
    onChange(value.includes(option) ? value.filter((v) => v !== option) : [...value, option]);

  return (
    <fieldset className={cn('w-full', className)}>
      <legend className="mb-2 block text-sm font-medium text-ink-800">
        {label}
        {required && <span className="ml-0.5 text-brand-600">*</span>}
      </legend>
      <div
        className="flex flex-wrap gap-2"
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
      >
        {options.map((option) => {
          const selected = value.includes(option.value);
          return (
            <button
              type="button"
              key={option.value}
              onClick={() => toggle(option.value)}
              aria-pressed={selected}
              className={cn(
                'rounded-full border px-3.5 py-2 text-xs font-medium transition-all duration-200',
                selected
                  ? 'border-brand-600 bg-brand-600 text-white shadow-soft'
                  : 'border-ink-200 bg-white text-ink-600 hover:border-brand-400 hover:text-brand-700'
              )}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      {error ? (
        <p id={`${id}-error`} className="mt-2 flex items-center gap-1.5 text-xs font-medium text-red-600">
          <AlertCircle className="h-3.5 w-3.5 flex-none" />
          {error}
        </p>
      ) : (
        hint && <p className="mt-2 text-xs text-ink-400">{hint}</p>
      )}
    </fieldset>
  );
}
